import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
    ShieldCheck, Zap, RefreshCw, FileText, PlugZap, Lock,
    ArrowRight, CheckCircle2, Mail, Boxes, Clock, TrendingUp,
} from 'lucide-react';
import { COMPANY } from '@/lib/legalContent';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/5e2d68cf-3365-4bbd-a422-8b6d6a2fc202/images/40ed2965b056683738d5fa1261a83f2303be31d1f5cdd9cf757ea14c6094940d.jpeg';
const MOCKUP_IMG = 'https://static.prod-images.emergentagent.com/jobs/5e2d68cf-3365-4bbd-a422-8b6d6a2fc202/images/ddc1e8194212f7acc78c7396f757748fafe236ebe9b34598978f031dc2e34daf.jpeg';
// Email di supporto della landing page (Fatture in Cloud). Indipendente dall'email
// legale del titolare usata nei documenti legali (legalContent.COMPANY.email).
const SUPPORT_EMAIL = 'infomniglide@gmail.com';

const FEATURES = [
    { icon: FileText, title: 'Invio automatico dei documenti', text: 'Ordini, DDT e fatture generati in OmniGlide vengono inviati automaticamente al tuo gestionale, senza reinserimenti manuali.' },
    { icon: RefreshCw, title: 'Anagrafiche e catalogo sincronizzati', text: 'Clienti, articoli, listini prezzi e giacenze restano allineati tra OmniGlide e il tuo account, riducendo gli errori.' },
    { icon: PlugZap, title: 'Collegamento in pochi clic', text: 'Attivi l’integrazione con un collegamento sicuro dal pannello: nessuna configurazione tecnica complessa.' },
    { icon: Zap, title: 'Sincronizzazione programmabile', text: 'Scegli l’invio automatico giornaliero all’orario che preferisci, oppure invia i documenti quando vuoi tu.' },
];

const BENEFITS = [
    { icon: Clock, title: 'Risparmi tempo ogni giorno', text: 'Elimini la doppia digitazione: gli ordini diventano documenti fiscali con un clic.' },
    { icon: CheckCircle2, title: 'Meno errori di trascrizione', text: 'I dati passano già strutturati, così importi, quantità e anagrafiche restano corretti.' },
    { icon: TrendingUp, title: 'Amministrazione sempre aggiornata', text: 'La contabilità riflette in tempo reale ciò che accade nel magazzino e nelle consegne.' },
    { icon: Boxes, title: 'Un unico flusso di lavoro', text: 'Dalla presa dell’ordine alla fattura, tutto parte da OmniGlide.' },
];

const STEPS = [
    { n: '1', title: 'Collega il tuo account', text: 'Dal pannello Integrazioni avvii il collegamento sicuro tramite autorizzazione OAuth 2.0.' },
    { n: '2', title: 'Configura le preferenze', text: 'Imposta il tipo di documento per cliente e l’orario di sincronizzazione automatica.' },
    { n: '3', title: 'Lavora come sempre', text: 'Gli ordini che gestisci in OmniGlide vengono inviati automaticamente al gestionale.' },
];

export default function FattureInCloudLanding() {
    return (
        <div className="min-h-screen bg-white text-[#0F172A]" data-testid="fic-landing-page">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[#E2E8F0]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <span className="text-xl font-bold text-brand" style={{ fontFamily: 'Work Sans, sans-serif' }}>{COMPANY.product}</span>
                    <div className="flex items-center gap-2">
                        <Link to="/login"><Button variant="ghost" className="text-brand" data-testid="fic-landing-login">Accedi</Button></Link>
                        <Link to="/signup"><Button className="bg-brand" data-testid="fic-landing-cta-header">Prova gratis</Button></Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-10 grid lg:grid-cols-2 gap-10 items-center">
                <div>
                    <span className="inline-flex items-center gap-2 text-xs font-semibold text-brand bg-[#F2F5F2] rounded-full px-3 py-1">
                        <PlugZap className="h-3.5 w-3.5" /> Integrazione con Fatture in Cloud
                    </span>
                    <h1 className="mt-4 text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                        Dai tuoi ordini alle fatture, in automatico
                    </h1>
                    <p className="mt-4 text-base sm:text-lg text-[#475569] max-w-xl">
                        {COMPANY.product} è il gestionale per la distribuzione alimentare all’ingrosso che invia
                        automaticamente ordini, DDT e fatture al tuo account Fatture in Cloud. Meno lavoro manuale,
                        amministrazione sempre allineata.
                    </p>
                    <div className="mt-7 flex flex-wrap gap-3">
                        <Link to="/signup"><Button className="bg-brand h-12 px-6 text-base" data-testid="fic-landing-cta-hero">
                            Inizia ora <ArrowRight className="ml-2 h-4 w-4" />
                        </Button></Link>
                        <a href={`mailto:${SUPPORT_EMAIL}`}><Button variant="outline" className="border-brand text-brand h-12 px-6 text-base" data-testid="fic-landing-support">
                            <Mail className="mr-2 h-4 w-4" /> Contatta il supporto
                        </Button></a>
                    </div>
                </div>
                <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-sm bg-[#F8FAFC]">
                    <img src={HERO_IMG} alt="Sincronizzazione ordini e documenti" className="w-full h-auto object-cover" data-testid="fic-landing-hero-img" />
                </div>
            </section>

            {/* Features */}
            <section className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
                    <h2 className="text-lg md:text-xl font-bold text-center" style={{ fontFamily: 'Work Sans, sans-serif' }}>Cosa fa l’integrazione</h2>
                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {FEATURES.map((f, i) => (
                            <div key={i} className="bg-white rounded-xl border border-[#E2E8F0] p-5" data-testid={`fic-feature-${i}`}>
                                <div className="h-10 w-10 rounded-lg bg-brand flex items-center justify-center">
                                    <f.icon className="h-5 w-5 text-white" />
                                </div>
                                <h3 className="mt-3 font-semibold">{f.title}</h3>
                                <p className="mt-1.5 text-sm text-[#64748B]">{f.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mockup + benefits */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-2 gap-10 items-center">
                <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-sm order-last lg:order-first">
                    <img src={MOCKUP_IMG} alt="Pannello di sincronizzazione documenti in OmniGlide" className="w-full h-auto object-cover" data-testid="fic-landing-mockup-img" />
                </div>
                <div>
                    <h2 className="text-lg md:text-xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Perché conviene</h2>
                    <div className="mt-6 space-y-5">
                        {BENEFITS.map((b, i) => (
                            <div key={i} className="flex gap-3" data-testid={`fic-benefit-${i}`}>
                                <b.icon className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-semibold">{b.title}</p>
                                    <p className="text-sm text-[#64748B]">{b.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
                    <h2 className="text-lg md:text-xl font-bold text-center" style={{ fontFamily: 'Work Sans, sans-serif' }}>Come funziona</h2>
                    <div className="mt-8 grid sm:grid-cols-3 gap-5">
                        {STEPS.map((s) => (
                            <div key={s.n} className="bg-white rounded-xl border border-[#E2E8F0] p-6" data-testid={`fic-step-${s.n}`}>
                                <div className="h-9 w-9 rounded-full bg-brand text-white flex items-center justify-center font-bold">{s.n}</div>
                                <h3 className="mt-3 font-semibold">{s.title}</h3>
                                <p className="mt-1.5 text-sm text-[#64748B]">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Security */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
                <div className="rounded-2xl border border-[#E2E8F0] p-6 sm:p-8 bg-white">
                    <div className="flex items-center gap-2 text-brand">
                        <ShieldCheck className="h-5 w-5" />
                        <h2 className="text-lg md:text-xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Sicurezza e privacy</h2>
                    </div>
                    <div className="mt-5 grid sm:grid-cols-3 gap-5">
                        <div className="flex gap-3" data-testid="fic-security-oauth">
                            <Lock className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                            <div><p className="font-semibold text-sm">Autorizzazione OAuth 2.0</p><p className="text-sm text-[#64748B]">Il collegamento avviene tramite autorizzazione ufficiale: non chiediamo né conserviamo le tue credenziali.</p></div>
                        </div>
                        <div className="flex gap-3" data-testid="fic-security-encryption">
                            <ShieldCheck className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                            <div><p className="font-semibold text-sm">Dati protetti</p><p className="text-sm text-[#64748B]">I token di accesso sono cifrati e custoditi lato server; ogni azienda ha i propri dati isolati.</p></div>
                        </div>
                        <div className="flex gap-3" data-testid="fic-security-reliable">
                            <RefreshCw className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                            <div><p className="font-semibold text-sm">Invio affidabile</p><p className="text-sm text-[#64748B]">Gestione automatica dei limiti e dei tentativi per un invio dei documenti stabile e senza perdite.</p></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA + support */}
            <section className="bg-brand">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 text-center text-white">
                    <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Pronto a semplificare la tua amministrazione?</h2>
                    <p className="mt-3 text-white/85 max-w-xl mx-auto">Collega {COMPANY.product} al tuo gestionale e lascia che ordini, DDT e fatture viaggino da soli.</p>
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                        <Link to="/signup"><Button className="bg-white text-brand hover:bg-white/90 h-12 px-6 text-base font-semibold" data-testid="fic-landing-cta-footer">Prova {COMPANY.product} gratis</Button></Link>
                        <a href={`mailto:${SUPPORT_EMAIL}`}><Button variant="outline" className="border-white text-white hover:bg-white/10 h-12 px-6 text-base" data-testid="fic-landing-support-footer">
                            <Mail className="mr-2 h-4 w-4" /> {SUPPORT_EMAIL}
                        </Button></a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-[#E2E8F0]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-sm text-[#64748B] flex flex-col sm:flex-row items-center justify-between gap-3">
                    <span>© {new Date().getFullYear()} {COMPANY.name} — {COMPANY.product}</span>
                    <div className="flex gap-4">
                        <Link to="/privacy" className="hover:text-brand">Privacy</Link>
                        <Link to="/terms" className="hover:text-brand">Termini</Link>
                        <Link to="/dpa" className="hover:text-brand">DPA</Link>
                        <Link to="/sub-responsabili" className="hover:text-brand">Sub-responsabili</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
